/**
 * Stamp Duty Calculator Utilities
 * 
 * This file contains all the calculation functions for Australian vehicle stamp duty
 * across different states and territories.
 */

import { 
  STAMP_DUTY_RULES, 
  StampDutyRules, 
  DutyTier, 
  DutyFlatRate, 
  DutyProgressiveTier,
  isDatePassed
} from '../data/stampDutyRules';

// Define interfaces for calculator inputs and results
export interface StampDutyInputs {
  state: string;
  vehicleValue: number;
  vehicleType: string;
  isNew: boolean;
  engineInfo?: string; // Cylinder count or emissions category
  concessions: string[];
}

export interface StampDutyResult {
  dutyAmount: number;
  baseAmount: number;
  discountAmount: number;
  rate: number | string;
  explanation: string[];
}

/**
 * Main function to calculate stamp duty based on inputs
 * 
 * @param inputs Object containing all required inputs for calculation
 * @returns StampDutyResult with calculation results and explanation
 */
export function calculateStampDuty(inputs: StampDutyInputs): StampDutyResult {
  const { state, vehicleValue, vehicleType, isNew, engineInfo, concessions } = inputs;
  
  // Check for full exemptions first
  if (qualifiesForFullExemption(state, concessions, vehicleType, vehicleValue)) {
    return {
      dutyAmount: 0,
      baseAmount: 0,
      discountAmount: 0,
      rate: '0%',
      explanation: ['Fully exempt from stamp duty based on applicable concessions.']
    };
  }

  // Calculate duty by state
  let dutyResult: StampDutyResult;
  
  switch (state) {
    case 'NSW':
      dutyResult = calculateNSWDuty(vehicleValue, vehicleType, isNew, concessions);
      break;
    case 'VIC':
      dutyResult = calculateVICDuty(vehicleValue, vehicleType, isNew, concessions);
      break;
    case 'QLD':
      dutyResult = calculateQLDDuty(vehicleValue, vehicleType, engineInfo, concessions);
      break;
    case 'WA':
      dutyResult = calculateWADuty(vehicleValue, vehicleType, concessions);
      break;
    case 'SA':
      dutyResult = calculateSADuty(vehicleValue, vehicleType, concessions);
      break;
    case 'TAS':
      dutyResult = calculateTASDuty(vehicleValue, vehicleType, concessions);
      break;
    case 'ACT':
      dutyResult = calculateACTDuty(vehicleValue, vehicleType, engineInfo, isNew, concessions);
      break;
    case 'NT':
      dutyResult = calculateNTDuty(vehicleValue, vehicleType, concessions);
      break;
    default:
      dutyResult = {
        dutyAmount: 0,
        baseAmount: 0, 
        discountAmount: 0,
        rate: '0%',
        explanation: ['Invalid state selected. Please choose a valid Australian state or territory.']
      };
  }

  // Apply minimum duty amount if applicable
  const stateRules = STAMP_DUTY_RULES[state];
  if (stateRules && stateRules.minimumDuty && dutyResult.dutyAmount > 0 && dutyResult.dutyAmount < stateRules.minimumDuty) {
    dutyResult.explanation.push(`Minimum duty amount of $${stateRules.minimumDuty.toFixed(2)} applied.`);
    dutyResult.dutyAmount = stateRules.minimumDuty;
  }

  return dutyResult;
}

/**
 * Check if the vehicle qualifies for full exemption from stamp duty
 */
function qualifiesForFullExemption(
  state: string, 
  concessions: string[], 
  vehicleType: string, 
  vehicleValue: number
): boolean {
  const stateRules = STAMP_DUTY_RULES[state];
  if (!stateRules) return false;

  // Check for family transfer exemption
  if (concessions.includes('familyTransfer') && stateRules.exemptions.familyTransfer) {
    return true;
  }

  // Check for disability exemption
  if (concessions.includes('disability') && stateRules.exemptions.disability) {
    return true;
  }

  // Check for pensioner exemption
  if (concessions.includes('pensioner') && stateRules.exemptions.pensioner) {
    return true;
  }

  // Check for primary producer exemption
  if (concessions.includes('primaryProducer') && stateRules.exemptions.primaryProducer) {
    return true;
  }

  // Check for EV exemption
  if (concessions.includes('EVs')) {
    // Check if state has explicit EV rules
    if (stateRules.EVRules && stateRules.EVRules.isExempt) {
      // Check if the exemption end date has passed
      if (!stateRules.EVRules.endDate || !isDatePassed(stateRules.EVRules.endDate)) {
        return true;
      }
    }
    // Check if there's a general EV exemption in exemptions
    else if (stateRules.exemptions.EVExemption) {
      // Check if the exemption end date has passed
      if (!stateRules.exemptions.EVExemptionEndDate || !isDatePassed(stateRules.exemptions.EVExemptionEndDate)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Calculates stamp duty for New South Wales
 */
function calculateNSWDuty(
  vehicleValue: number, 
  vehicleType: string, 
  isNew: boolean,
  concessions: string[]
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['NSW'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  if (vehicleType === 'motorcycle' || vehicleType === 'commercial') {
    // Flat rate of 3% for motorcycles and commercial vehicles
    const rules = stateRules.commercial as DutyFlatRate;
    dutyAmount = vehicleValue * (rules.rate / 100);
    rate = `${rules.rate}%`;
    explanation.push(`${vehicleType === 'motorcycle' ? 'Motorcycle' : 'Commercial vehicle'} rate of ${rules.rate}% applied.`);
  } else {
    // Passenger vehicles - tiered rate
    const rules = stateRules.passenger as DutyTier[];
    const threshold = rules[0].threshold;
    
    if (vehicleValue <= threshold) {
      dutyAmount = vehicleValue * (rules[0].rateBelow / 100);
      rate = `${rules[0].rateBelow}%`;
      explanation.push(`Vehicle value ($${vehicleValue.toFixed(2)}) is below the threshold of $${threshold.toFixed(2)}.`);
      explanation.push(`Applied rate of ${rules[0].rateBelow}% to the full amount.`);
    } else {
      dutyAmount = (threshold * (rules[0].rateBelow / 100)) + ((vehicleValue - threshold) * (rules[0].rateAbove / 100));
      rate = `${rules[0].rateBelow}% up to $${threshold}, then ${rules[0].rateAbove}%`;
      explanation.push(`Vehicle value ($${vehicleValue.toFixed(2)}) exceeds the threshold of $${threshold.toFixed(2)}.`);
      explanation.push(`Applied ${rules[0].rateBelow}% to first $${threshold.toFixed(2)}, then ${rules[0].rateAbove}% to the remainder.`);
    }
  }

  // Check for EV concession
  if (concessions.includes('EVs') && stateRules.exemptions.EVExemption) {
    if (stateRules.exemptions.EVExemptionEndDate && !isDatePassed(stateRules.exemptions.EVExemptionEndDate)) {
      const baseAmount = dutyAmount;
      discountAmount = baseAmount;
      dutyAmount = 0;
      explanation.push(`Electric vehicle exemption applied, valid until ${stateRules.exemptions.EVExemptionEndDate}.`);
    } else if (stateRules.exemptions.EVExemptionEndDate && isDatePassed(stateRules.exemptions.EVExemptionEndDate)) {
      explanation.push(`Electric vehicle exemption expired on ${stateRules.exemptions.EVExemptionEndDate}.`);
    }
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Calculates stamp duty for Victoria
 */
function calculateVICDuty(
  vehicleValue: number, 
  vehicleType: string, 
  isNew: boolean,
  concessions: string[]
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['VIC'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  if (vehicleType === 'motorcycle' || vehicleType === 'commercial') {
    // Different rates for new vs used non-passenger vehicles
    const rules = stateRules.commercial as DutyFlatRate;
    const rateToUse = isNew ? 2.7 : 4.2; // Victoria has different rates for new vs used commercial vehicles
    
    dutyAmount = vehicleValue * (rateToUse / 100);
    rate = `${rateToUse}%`;
    explanation.push(`Applied ${isNew ? 'new' : 'used'} ${vehicleType} rate of ${rateToUse}%.`);
  } else {
    // Passenger vehicles - progressive tiers
    const tiers = stateRules.passenger as DutyProgressiveTier[];
    
    // Find the applicable tier
    let applicableTier = tiers.find(tier => vehicleValue <= tier.upTo);
    
    if (!applicableTier) {
      applicableTier = tiers[tiers.length - 1]; // Use the highest tier if value exceeds all tiers
    }
    
    dutyAmount = vehicleValue * (applicableTier.rate / 100);
    rate = `${applicableTier.rate}%`;
    explanation.push(`Vehicle value of $${vehicleValue.toFixed(2)} falls in the tier with rate ${applicableTier.rate}%.`);
  }

  // Check for EV concession
  if (concessions.includes('EVs') && stateRules.EVRules && stateRules.EVRules.isExempt) {
    if (!stateRules.EVRules.endDate || !isDatePassed(stateRules.EVRules.endDate)) {
      const baseAmount = dutyAmount;
      discountAmount = baseAmount;
      dutyAmount = 0;
      explanation.push(`Electric vehicle exemption applied${stateRules.EVRules.endDate ? `, valid until ${stateRules.EVRules.endDate}` : ''}.`);
    } else if (stateRules.EVRules.endDate && isDatePassed(stateRules.EVRules.endDate)) {
      explanation.push(`Electric vehicle exemption expired on ${stateRules.EVRules.endDate}.`);
    }
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Calculates stamp duty for Queensland
 */
function calculateQLDDuty(
  vehicleValue: number, 
  vehicleType: string, 
  engineInfo?: string,
  concessions: string[] = []
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['QLD'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  if (vehicleType === 'motorcycle' || vehicleType === 'commercial') {
    // Flat rate for motorcycles and commercial vehicles
    const rules = stateRules.commercial as DutyFlatRate;
    dutyAmount = vehicleValue * (rules.rate / 100);
    rate = `${rules.rate}%`;
    explanation.push(`Applied flat rate of ${rules.rate}% for ${vehicleType}.`);
  } else if (vehicleType === 'passenger' && stateRules.cylinderRules && engineInfo) {
    // Queensland has cylinder-based tiers for passenger vehicles
    const cylinderRules = stateRules.cylinderRules[engineInfo];
    
    if (cylinderRules) {
      const { threshold, belowThreshold, aboveThreshold } = cylinderRules;
      
      if (vehicleValue <= threshold) {
        dutyAmount = vehicleValue * (belowThreshold / 100);
        rate = `${belowThreshold}%`;
        explanation.push(`Vehicle with ${engineInfo} engine is valued at $${vehicleValue.toFixed(2)}, below the $${threshold.toFixed(2)} threshold.`);
        explanation.push(`Applied rate of ${belowThreshold}%.`);
      } else {
        dutyAmount = vehicleValue * (aboveThreshold / 100);
        rate = `${aboveThreshold}%`;
        explanation.push(`Vehicle with ${engineInfo} engine is valued at $${vehicleValue.toFixed(2)}, above the $${threshold.toFixed(2)} threshold.`);
        explanation.push(`Applied rate of ${aboveThreshold}%.`);
      }
    } else {
      // Fallback if cylinder info doesn't match known categories
      const defaultRule = stateRules.passenger as DutyFlatRate;
      dutyAmount = vehicleValue * (defaultRule.rate / 100);
      rate = `${defaultRule.rate}%`;
      explanation.push(`No specific cylinder rule found for '${engineInfo}'. Applied default rate of ${defaultRule.rate}%.`);
    }
  } else {
    // Fallback to basic passenger vehicle rate if no engine info provided
    const defaultRule = stateRules.passenger as DutyFlatRate;
    dutyAmount = vehicleValue * (defaultRule.rate / 100);
    rate = `${defaultRule.rate}%`;
    explanation.push(`Applied default passenger vehicle rate of ${defaultRule.rate}%.`);
    explanation.push('Note: Queensland uses cylinder count to determine duty rates. For accurate calculation, please provide engine information.');
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Calculates stamp duty for Western Australia
 */
function calculateWADuty(
  vehicleValue: number, 
  vehicleType: string,
  concessions: string[]
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['WA'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  if (vehicleType === 'motorcycle') {
    // Flat rate for motorcycles
    const rules = stateRules.motorcycle as DutyFlatRate;
    dutyAmount = vehicleValue * (rules.rate / 100);
    rate = `${rules.rate}%`;
    explanation.push(`Applied flat rate of ${rules.rate}% for motorcycles.`);
  } else if (vehicleType === 'commercial') {
    // Flat rate for commercial vehicles
    const rules = stateRules.commercial as DutyFlatRate;
    dutyAmount = vehicleValue * (rules.rate / 100);
    rate = `${rules.rate}%`;
    explanation.push(`Applied flat rate of ${rules.rate}% for commercial vehicles.`);
  } else {
    // Passenger vehicles - progressive tiers
    const tiers = stateRules.passenger as DutyProgressiveTier[];
    
    // Find the applicable tier
    let applicableTier = tiers.find(tier => vehicleValue <= tier.upTo);
    
    if (!applicableTier) {
      applicableTier = tiers[tiers.length - 1]; // Use the highest tier if value exceeds all tiers
    }
    
    dutyAmount = vehicleValue * (applicableTier.rate / 100);
    rate = `${applicableTier.rate}%`;
    explanation.push(`Vehicle value of $${vehicleValue.toFixed(2)} falls in the tier with rate ${applicableTier.rate}%.`);
  }

  // Check for EV concession in WA
  if (concessions.includes('EVs') && stateRules.EVRules && stateRules.EVRules.discountAmount) {
    discountAmount = Math.min(dutyAmount, stateRules.EVRules.discountAmount);
    dutyAmount -= discountAmount;
    explanation.push(`Electric vehicle discount of $${stateRules.EVRules.discountAmount.toFixed(2)} applied.`);
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Calculates stamp duty for South Australia
 */
function calculateSADuty(
  vehicleValue: number, 
  vehicleType: string,
  concessions: string[]
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['SA'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  if (vehicleType === 'motorcycle' || vehicleType === 'commercial') {
    // Flat rate for motorcycles and commercial vehicles
    const rules = vehicleType === 'motorcycle' ? stateRules.motorcycle as DutyFlatRate : stateRules.commercial as DutyFlatRate;
    dutyAmount = vehicleValue * (rules.rate / 100);
    rate = `${rules.rate}%`;
    explanation.push(`Applied flat rate of ${rules.rate}% for ${vehicleType}.`);
  } else {
    // Passenger vehicles - progressive tiers
    const tiers = stateRules.passenger as DutyProgressiveTier[];
    
    // Find the applicable tier
    let applicableTier = tiers.find(tier => vehicleValue <= tier.upTo);
    
    if (!applicableTier) {
      applicableTier = tiers[tiers.length - 1]; // Use the highest tier if value exceeds all tiers
    }
    
    dutyAmount = vehicleValue * (applicableTier.rate / 100);
    rate = `${applicableTier.rate}%`;
    explanation.push(`Vehicle value of $${vehicleValue.toFixed(2)} falls in the tier with rate ${applicableTier.rate}%.`);
  }

  // Check for EV concession
  if (concessions.includes('EVs') && stateRules.EVRules && stateRules.EVRules.isExempt) {
    if (!stateRules.EVRules.endDate || !isDatePassed(stateRules.EVRules.endDate)) {
      const baseAmount = dutyAmount;
      discountAmount = baseAmount;
      dutyAmount = 0;
      explanation.push(`Electric vehicle exemption applied${stateRules.EVRules.endDate ? `, valid until ${stateRules.EVRules.endDate}` : ''}.`);
    } else if (stateRules.EVRules.endDate && isDatePassed(stateRules.EVRules.endDate)) {
      explanation.push(`Electric vehicle exemption expired on ${stateRules.EVRules.endDate}.`);
    }
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Calculates stamp duty for Tasmania
 */
function calculateTASDuty(
  vehicleValue: number, 
  vehicleType: string,
  concessions: string[]
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['TAS'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  // Tasmania has a flat rate for all vehicle types
  const vehicleRule = stateRules[vehicleType as keyof typeof stateRules] as DutyFlatRate || stateRules.passenger as DutyFlatRate;
  dutyAmount = vehicleValue * (vehicleRule.rate / 100);
  rate = `${vehicleRule.rate}%`;
  explanation.push(`Applied flat rate of ${vehicleRule.rate}% for ${vehicleType} in Tasmania.`);

  // Check for EV concession
  if (concessions.includes('EVs') && stateRules.EVRules && stateRules.EVRules.isExempt) {
    if (!stateRules.EVRules.endDate || !isDatePassed(stateRules.EVRules.endDate)) {
      const baseAmount = dutyAmount;
      discountAmount = baseAmount;
      dutyAmount = 0;
      explanation.push(`Electric vehicle exemption applied${stateRules.EVRules.endDate ? `, valid until ${stateRules.EVRules.endDate}` : ''}.`);
    } else if (stateRules.EVRules.endDate && isDatePassed(stateRules.EVRules.endDate)) {
      explanation.push(`Electric vehicle exemption expired on ${stateRules.EVRules.endDate}.`);
    }
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Calculates stamp duty for Australian Capital Territory
 */
function calculateACTDuty(
  vehicleValue: number, 
  vehicleType: string, 
  engineInfo?: string,
  isNew: boolean = true,
  concessions: string[] = []
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['ACT'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  if (vehicleType === 'motorcycle' || vehicleType === 'commercial') {
    // Flat rate for motorcycles and commercial vehicles
    const rules = vehicleType === 'motorcycle' ? stateRules.motorcycle as DutyFlatRate : stateRules.commercial as DutyFlatRate;
    dutyAmount = vehicleValue * (rules.rate / 100);
    rate = `${rules.rate}%`;
    explanation.push(`Applied flat rate of ${rules.rate}% for ${vehicleType}.`);
  } else if (vehicleType === 'passenger' && stateRules.CO2Rules && engineInfo) {
    // ACT uses CO2 emissions categories for passenger vehicles
    const co2Rule = stateRules.CO2Rules[engineInfo];
    
    if (co2Rule) {
      dutyAmount = vehicleValue * (co2Rule.rate / 100);
      rate = `${co2Rule.rate}%`;
      explanation.push(`Vehicle in CO2 emissions category ${engineInfo} (${co2Rule.range ? `${co2Rule.range[0]}-${co2Rule.range[1]} g/km` : 'custom range'}).`);
      explanation.push(`Applied rate of ${co2Rule.rate}%.`);
    } else {
      // Fallback if CO2 category doesn't match known categories
      explanation.push(`No specific CO2 emissions rule found for '${engineInfo}'. Applied default rate.`);
      dutyAmount = vehicleValue * 0.04; // Default to highest rate if category unknown
      rate = '4%';
    }
  } else {
    // Fallback message if CO2 emissions info not provided
    explanation.push('ACT uses CO2 emissions to determine duty rates. For accurate calculation, please provide emissions category.');
    dutyAmount = vehicleValue * 0.04; // Default to highest rate
    rate = '4% (default)';
  }

  // Check for EV concession
  if (concessions.includes('EVs') && stateRules.EVRules && stateRules.EVRules.isExempt) {
    if (!stateRules.EVRules.endDate || !isDatePassed(stateRules.EVRules.endDate)) {
      const baseAmount = dutyAmount;
      discountAmount = baseAmount;
      dutyAmount = 0;
      explanation.push(`Electric vehicle exemption applied${stateRules.EVRules.endDate ? `, valid until ${stateRules.EVRules.endDate}` : ''}.`);
    } else if (stateRules.EVRules.endDate && isDatePassed(stateRules.EVRules.endDate)) {
      explanation.push(`Electric vehicle exemption expired on ${stateRules.EVRules.endDate}.`);
    }
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Calculates stamp duty for Northern Territory
 */
function calculateNTDuty(
  vehicleValue: number, 
  vehicleType: string,
  concessions: string[]
): StampDutyResult {
  const stateRules = STAMP_DUTY_RULES['NT'];
  let dutyAmount = 0;
  let explanation: string[] = [];
  let rate: string | number = '';
  let discountAmount = 0;

  // NT has a flat rate for all vehicle types
  const vehicleRule = stateRules[vehicleType as keyof typeof stateRules] as DutyFlatRate || stateRules.passenger as DutyFlatRate;
  dutyAmount = vehicleValue * (vehicleRule.rate / 100);
  rate = `${vehicleRule.rate}%`;
  explanation.push(`Applied flat rate of ${vehicleRule.rate}% for all vehicles in Northern Territory.`);

  // Check for EV concession
  if (concessions.includes('EVs') && stateRules.EVRules && stateRules.EVRules.discountAmount) {
    discountAmount = Math.min(dutyAmount, stateRules.EVRules.discountAmount);
    dutyAmount -= discountAmount;
    explanation.push(`Electric vehicle discount of $${stateRules.EVRules.discountAmount.toFixed(2)} applied.`);
  }

  return {
    dutyAmount,
    baseAmount: dutyAmount + discountAmount,
    discountAmount,
    rate,
    explanation
  };
}

/**
 * Format currency value to Australian dollar format
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
} 